/**
 * Secure Token Manager
 * 
 * Stores JWT token in memory (JS closure) with localStorage for persistence across page refreshes.
 * Provides centralized access to the auth token, reducing direct localStorage reads.
 * Validates token expiry on retrieval to prevent use of stale tokens.
 */

import logger from './logger';

const TOKEN_KEY = 'auth_token';

// In-memory token store - primary source of truth
let memoryToken = null;

/**
 * Initialize token from localStorage into memory on app start.
 * Call this once in App.js or index.js.
 */
export function initToken() {
  const stored = localStorage.getItem(TOKEN_KEY);
  if (stored) {
    if (isTokenExpired(stored)) {
      clearToken();
    } else {
      memoryToken = stored;
    }
  }
}

/**
 * Get the current auth token. 
 * Returns from memory first, falls back to localStorage.
 * Returns null if token is expired.
 */
export function getToken() {
  if (memoryToken) {
    if (isTokenExpired(memoryToken)) {
      clearToken();
      return null;
    }
    return memoryToken;
  }

  // Fallback: check localStorage (e.g., after memory is cleared but page not reloaded)
  const stored = localStorage.getItem(TOKEN_KEY);
  if (stored && !isTokenExpired(stored)) {
    memoryToken = stored;
    return stored;
  }

  if (stored) {
    clearToken();
  }
  return null;
}

/**
 * Store a new token. Saves to both memory and localStorage for persistence.
 */
export function setToken(token) {
  memoryToken = token;
  localStorage.setItem(TOKEN_KEY, token);
}

/**
 * Clear token from both memory and localStorage.
 */
export function clearToken() {
  memoryToken = null;
  localStorage.removeItem(TOKEN_KEY);
}

/**
 * Build auth headers using the current token.
 */
export function getAuthHeaders() {
  const token = getToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

/**
 * Check if a JWT token is expired by decoding the payload.
 * Returns true if expired or if the token is malformed.
 */
function isTokenExpired(token) {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return true;

    const payload = JSON.parse(atob(parts[1]));
    if (!payload.exp) return false; // No expiry claim = treat as valid

    // Add 30s buffer to avoid edge-case race conditions
    return Date.now() >= (payload.exp * 1000) - 30000;
  } catch (err) {
    logger.error(err);
    return true; // Malformed token = treat as expired
  }
}
